import React, { useState, useEffect, useMemo } from "react"
import i18n from "i18n"

import Box from "@mui/material/Box"
import { DataGridPro } from "@mui/x-data-grid-pro"
import PropTypes from "prop-types"
import {
  calculateDimensions,
  calculateDimensionTotal,
  calculatePercentage,
  validateCertCurrent,
} from "./utils"
import MeasuresFooter from "./MeasuresFooter"

const MeasuresDataGrid = ({
  measures,
  onMeasuresUpdate,
  measureCertCheckboxChecked,
  onMeasureCertCheckboxChange,
  measureCertCheckboxDisabled,
}) => {
  const [localMeasures, setLocalMeasures] = useState(measures || [])

  useEffect(() => {
    setLocalMeasures(measures || [])
  }, [measures])

  const CustomFooter = () => (
    <MeasuresFooter
      measures={localMeasures}
      measureCertCheckboxChecked={measureCertCheckboxChecked}
      onMeasureCertCheckboxChange={onMeasureCertCheckboxChange}
      measureCertCheckboxDisabled={measureCertCheckboxDisabled}
    />
  )

  const handleCellEditCommit = params => {
    if (params.field === "units") {
      const measure = localMeasures[params.id]
      if (measure) {
        const originalValue = params.value
        const validatedValue = validateCertCurrent(originalValue, measure)

        const updatedMeasures = localMeasures.map((m, index) => {
          if (index === params.id) {
            return { ...m, cert_current: validatedValue }
          }
          return m
        })

        setLocalMeasures(updatedMeasures)
        onMeasuresUpdate?.(updatedMeasures)
      }
    }
  }

  const handleProcessRowUpdate = (newRow, oldRow) => {
    if (newRow.units !== oldRow.units) {
      const measure = localMeasures[newRow.id]
      if (measure) {
        const originalValue = newRow.units
        const validatedValue = validateCertCurrent(originalValue, measure)

        const updatedMeasures = localMeasures.map((m, index) => {
          if (index === newRow.id) {
            return { ...m, cert_current: validatedValue }
          }
          return m
        })

        setLocalMeasures(updatedMeasures)
        onMeasuresUpdate?.(updatedMeasures)

        return { ...newRow, units: validatedValue }
      }
    }
    return newRow
  }

  const columns = [
    {
      field: "description",
      headerName: i18n.t("certMeasurements.description"),
      flex: 1,
      minWidth: 200,
      cellClassName: "non-editable-cell",
    },
    {
      field: "quantity",
      headerName: i18n.t("certMeasurements.quantity"),
      type: "number",
      width: 80,
      align: "center",
      headerAlign: "center",
      cellClassName: "non-editable-cell",
    },
    {
      field: "dimensions",
      headerName: i18n.t("certMeasurements.dimensions"),
      width: 110,
      align: "center",
      headerAlign: "center",
      valueGetter: (value, row) => calculateDimensions(row),
      cellClassName: "non-editable-cell",
    },
    {
      field: "total",
      headerName: i18n.t("certMeasurements.total"),
      type: "number",
      width: 60,
      valueGetter: (value, row) => calculateDimensionTotal(row),
      cellClassName: "non-editable-cell",
    },
    {
      field: "previousPercentage",
      headerName: "%",
      type: "number",
      width: 70,
      align: "center",
      headerAlign: "center",
      editable: false,
      valueGetter: (value, row) => {
        const measure = localMeasures[row.id]
        if (measure) {
          const total = calculateDimensionTotal(measure)
          const certPrevious = measure.cert_previous || 0
          if (total === 0 || !certPrevious) return 0
          const percent = (certPrevious / total) * 100
          return Number(percent.toFixed(2))
        }
        return 0
      },
      valueFormatter: value => {
        if (typeof value === "number") {
          if (value === 100) return "100%"
          return `${value.toFixed(2)}%`
        }
        return `${value}%`
      },
      cellClassName: params => {
        const percentage = params.value || 0
        if (percentage === 100) {
          return "percentage-complete"
        } else if (percentage > 0) {
          return "percentage-partial"
        }
        return "non-editable-cell"
      },
    },
    {
      field: "previousUnits",
      headerName: i18n.t("certMeasurements.uds"),
      type: "number",
      width: 90,
      align: "center",
      headerAlign: "center",
      editable: false,
      valueGetter: (value, row) => {
        const measure = localMeasures[row.id]
        if (measure) {
          return measure.cert_previous || 0
        }
        return row.cert_previous || 0
      },
      cellClassName: params => {
        const measure = localMeasures[params.row.id]
        if (measure) {
          const total = calculateDimensionTotal(measure)
          const certPrevious = measure.cert_previous || 0
          if (certPrevious === total && total > 0) {
            return "units-complete"
          } else if (certPrevious > 0) {
            return "units-partial"
          }
        }
        return "non-editable-cell"
      },
    },
    {
      field: "percentage",
      headerName: "%",
      type: "number",
      width: 70,
      align: "center",
      headerAlign: "center",
      editable: false,
      valueGetter: (value, row) => {
        const measure = localMeasures[row.id]
        if (measure) {
          return Number(calculatePercentage(measure).toFixed(2))
        }
        return Number(calculatePercentage(row).toFixed(2))
      },
      valueFormatter: value => {
        if (typeof value === "number") {
          if (value === 100) return "100%"
          return `${value.toFixed(2)}%`
        }
        return `${value}%`
      },
      cellClassName: params => {
        const percentage = params.value || 0
        if (percentage === 100) {
          return "percentage-complete"
        } else if (percentage > 0) {
          return "percentage-partial"
        }
        return "non-editable-cell"
      },
    },
    {
      field: "units",
      headerName: i18n.t("certMeasurements.uds"),
      type: "number",
      width: 60,
      align: "center",
      headerAlign: "center",
      editable: true,
      cellClassName: params => {
        const measure = localMeasures[params.row.id]
        if (measure) {
          const total = calculateDimensionTotal(measure)
          const certCurrent = measure.cert_current || 0
          if (certCurrent === total && total > 0) {
            return "units-complete"
          } else if (certCurrent > 0) {
            return "units-partial"
          }
        }
        return ""
      },
    },
  ]

  const columnGroupingModel = [
    {
      groupId: "mediciones",
      headerName: i18n.t("certMeasurements.measurements"),
      children: [
        { field: "description" },
        { field: "quantity" },
        { field: "dimensions" },
        { field: "total" },
      ],
    },
    {
      groupId: "certificacionPrev",
      headerName: i18n.t("certMeasurements.certPrevious"),
      children: [{ field: "previousUnits" }, { field: "previousPercentage" }],
    },
    {
      groupId: "certificacion",
      headerName: i18n.t("certMeasurements.certCurrent"),
      children: [{ field: "percentage" }, { field: "units" }],
    },
  ]

  const rows = useMemo(() => {
    return (
      localMeasures?.map((measure, index) => ({
        id: index,
        ...measure,
        units: measure.cert_current || 0,
        previousUnits: measure.cert_previous || 0,
      })) || []
    )
  }, [localMeasures])

  return (
    <div style={{ width: "100%" }}>
      <Box
        sx={{
          height: 400,
          "& .css-g39zud-MuiDataGrid-root .MuiDataGrid-columnHeader:hover .MuiDataGrid-iconButtonContainer":
            {
              display: "none",
            },
          "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-iconButtonContainer":
            {
              display: "none",
            },
          "& .non-editable-cell": {
            backgroundColor: "#f5f5f5",
          },
          "& .percentage-complete": {
            backgroundColor: "#e8f5e8",
            color: "#2e7d32",
          },
          "& .percentage-partial": {
            backgroundColor: "#fff3e0",
            color: "#f57c00",
          },
          "& .units-complete": {
            backgroundColor: "#e8f5e8",
            color: "#2e7d32",
          },
          "& .units-partial": {
            backgroundColor: "#fff3e0",
            color: "#f57c00",
          },
        }}
      >
        <DataGridPro
          rows={rows}
          columns={columns}
          columnGroupingModel={columnGroupingModel}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          density="compact"
          disableColumnMenu
          disableColumnSelector
          disableRowSelectionOnClick
          sortingOrder={[]}
          onCellEditCommit={handleCellEditCommit}
          processRowUpdate={handleProcessRowUpdate}
          onProcessRowUpdateError={error => {
            console.error("Error processing row update:", error)
          }}
          pinnedColumns={{ right: ["percentage", "units"] }}
          slots={{
            footer: CustomFooter,
          }}
        />
      </Box>
    </div>
  )
}

MeasuresDataGrid.propTypes = {
  measures: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      depth: PropTypes.number,
      height: PropTypes.number,
      width: PropTypes.number,
      quantity: PropTypes.number,
      cert_current: PropTypes.number,
      cert_previous: PropTypes.number,
    }),
  ),
  onMeasuresUpdate: PropTypes.func,
  measureCertCheckboxChecked: PropTypes.bool,
  onMeasureCertCheckboxChange: PropTypes.func,
  measureCertCheckboxDisabled: PropTypes.bool,
}

export default MeasuresDataGrid
